// utils/passwordValidation.js

/**
 * パスワードバリデーション関数
 * 要件：8文字以上、大文字・小文字・数字・特殊文字のうち3種類以上
 * @param {string} password - バリデーション対象のパスワード
 * @returns {Array} エラーメッセージの配列（エラーがない場合は空配列）
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password) {
    return errors; // 空の場合はバリデーションしない
  }
  
  // 文字数チェック
  if (password.length < 8) {
    errors.push('パスワードは8文字以上で入力してください');
  }
  
  // 文字種チェック
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  
  // 4種類のうち何種類含まれているかカウント
  const charTypeCount = [hasLowercase, hasUppercase, hasNumbers, hasSpecial].filter(Boolean).length;
  
  if (charTypeCount < 3) {
    errors.push('大文字、小文字、数字、特殊文字のうち3種類以上を含む必要があります');
  }
  
  return errors;
};

/**
 * パスワード確認バリデーション関数
 * @param {string} password - パスワード
 * @param {string} confirmPassword - 確認用パスワード
 * @returns {boolean} パスワードが一致するかどうか
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  return password === confirmPassword;
};

/**
 * パスワードが完全に有効かどうかをチェック
 * @param {string} password - チェック対象のパスワード
 * @returns {boolean} 全ての要件を満たしているかどうか
 */
export const isPasswordValid = (password) => {
  const errors = validatePassword(password);
  return errors.length === 0;
};

/**
 * パスワード要件のテキスト
 */
export const PASSWORD_REQUIREMENTS = 'パスワードは8文字以上で、大文字・小文字・数字・特殊文字のうち3種類以上を含む必要があります';

/**
 * バリデーションエラーメッセージの定数
 */
export const VALIDATION_MESSAGES = {
  PASSWORD_MISMATCH: 'パスワードと再確認パスワードが一致しません',
  PASSWORD_REQUIREMENTS_NOT_MET: 'パスワードの要件を満たしていません',
  PASSWORD_REQUIRED: 'パスワードを入力してください',
  PASSWORD_TOO_SHORT: 'パスワードが短すぎます',
  CHAR_TYPES_INSUFFICIENT: '大文字、小文字、数字、特殊文字のうち3種類以上を含む必要があります'
};

/**
 * 使用可能な特殊文字の定数
 */
export const SPECIAL_CHARACTERS = '!@#$%^&*()_+-=[]{}|;:,.<>?';

/**
 * デフォルトのパスワード設定（3種類以上対応）
 */
export const DEFAULT_PASSWORD_CONFIG = {
  minLength: 8,
  requireCharTypes: 3, // 最低必要な文字種の数
  requireUppercase: false, // 個別の必須要件ではなく、種類数で判定
  requireLowercase: false,
  requireNumbers: false,
  requireSpecial: false,
  maxLength: 128
};

/**
 * カスタム設定でのパスワードバリデーション
 * @param {string} password - チェック対象のパスワード
 * @param {object} config - バリデーション設定
 * @returns {Array} エラーメッセージの配列
 */
export const validatePasswordWithConfig = (password, config = DEFAULT_PASSWORD_CONFIG) => {
  const errors = [];
  
  if (!password) {
    return errors;
  }
  
  const {
    minLength = 8,
    maxLength = 128,
    requireCharTypes = 3,
    requireUppercase = false,
    requireLowercase = false,
    requireNumbers = false,
    requireSpecial = false
  } = config;
  
  if (password.length < minLength) {
    errors.push(`パスワードは${minLength}文字以上で入力してください`);
  }
  
  if (password.length > maxLength) {
    errors.push(`パスワードは${maxLength}文字以下で入力してください`);
  }
  
  // 文字種チェック
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  
  // 個別必須要件のチェック（後方互換性のため）
  if (requireLowercase && !hasLowercase) {
    errors.push('小文字を含む必要があります');
  }
  
  if (requireUppercase && !hasUppercase) {
    errors.push('大文字を含む必要があります');
  }
  
  if (requireNumbers && !hasNumbers) {
    errors.push('数字を含む必要があります');
  }
  
  if (requireSpecial && !hasSpecial) {
    errors.push('特殊文字を含む必要があります');
  }
  
  // 文字種の多様性チェック
  if (requireCharTypes > 0) {
    const charTypeCount = [hasLowercase, hasUppercase, hasNumbers, hasSpecial].filter(Boolean).length;
    
    if (charTypeCount < requireCharTypes) {
      errors.push(`大文字、小文字、数字、特殊文字のうち${requireCharTypes}種類以上を含む必要があります`);
    }
  }
  
  return errors;
};